import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {retry} from 'rxjs/operators';
import {environment} from '@env/environment';
import {ApplicantModel, ApplicationModel, CSPostResponseModel, EmploymentModel, ResidenceModel} from '@service/models';
import {ExtendedAttributesModel} from '@service/models/extendedAttributes.model';

const API_APPLICANT_URL = environment.apiUrl + '/applicant';
const API_APPLICATION_URL = environment.depositApiUrl + '/application';

@Injectable({
    providedIn: 'root'
})
export class ApplicantService {

    constructor(private http: HttpClient) {
    }

    addApplicant(_applicationId: number, _applicant: ApplicantModel): Observable<CSPostResponseModel> {
        return this.http.post<CSPostResponseModel>(API_APPLICANT_URL + `/${_applicationId}`, _applicant);
    }

    addExtendedAttributesAtApplicantLevel(applicantId: number, extendedAttributes: ExtendedAttributesModel[]): Observable<CSPostResponseModel> {
        return this.http.post<CSPostResponseModel>(environment.apiUrl + `/applicant/extendedattributes/${applicantId}`, extendedAttributes);
    }

    updateApplicant(_applicationId: number, applicant: ApplicantModel): Observable<CSPostResponseModel> {
        return this.http.put<CSPostResponseModel>(API_APPLICANT_URL + `/${_applicationId}`, applicant);
    }

    findApplicant(_applicantId: number, isCoBoSearch = false): Observable<ApplicantModel> {
        const url = (isCoBoSearch) ? environment.apiUrl + '/coapplicant/details' : API_APPLICANT_URL;
        return this.http.get<ApplicantModel>(url + `/${_applicantId}`).pipe(
            retry(3));
    }

    // the applicants will be part of the data attribute, this can be one or collection
    findLoanApplicants(_applicantionId: number): Observable<ApplicationModel> {
        return this.http.get<ApplicationModel>(API_APPLICANT_URL + `/${_applicantionId}`).pipe(
            retry(3));
    }

    addEmployment(_employment: EmploymentModel, _applicantId: number): Observable<CSPostResponseModel> {
        return this.http.post<CSPostResponseModel>(API_APPLICANT_URL + `/employment/${_applicantId}`, _employment);
    }

    addResidence(residence: ResidenceModel, _applicantId: number): Observable<CSPostResponseModel> {
        return this.http.post<CSPostResponseModel>(API_APPLICANT_URL + `/residence/${_applicantId}`, residence);
    }

    deleteEmp(_empId: number, _applicantId: number): Observable<CSPostResponseModel> {
        return this.http.delete<CSPostResponseModel>(API_APPLICANT_URL + `/employment/${_applicantId}/${_empId}`);
    }

    deleteBusinessOwner(businessOwnerApplicantId: number, applicationId: number): Observable<CSPostResponseModel> {
        return this.http.delete<CSPostResponseModel>(API_APPLICANT_URL + `/businessowner/${businessOwnerApplicantId}/application/${applicationId}`);
    }

    getReference(_applicationId: number): Observable<CSPostResponseModel> {
        return this.http.get<CSPostResponseModel>(API_APPLICATION_URL + `/${_applicationId}/reference`);
    }

    updateReference(_applicationId: number, referenceBody: any): Observable<CSPostResponseModel> {
        return this.http.post<CSPostResponseModel>(environment.apiUrl + `/application/${_applicationId}/reference`, referenceBody);
    }

    updatePastResidence(_applicationId: number, residenceBoy: any): Observable<CSPostResponseModel> {
        return this.http.post<CSPostResponseModel>(API_APPLICANT_URL + `/pastresidence/${_applicationId}`, residenceBoy);
    }

    updateDeclarationQuestion(declarationBody: any): Observable<CSPostResponseModel> {
        return this.http.post<CSPostResponseModel>(environment.depositApiUrl + `/declaration`, declarationBody);
    }

    getDeclarationQuestion(_applicationId: number): Observable<any> {
        return this.http.get<any>(environment.depositApiUrl + `/declaration/${_applicationId}`);
    }

    updateAssetsLiabilities(assetsLiabilities: any): Observable<CSPostResponseModel> {
        return this.http.post<CSPostResponseModel>(environment.depositApiUrl + `/financialcondition`, assetsLiabilities);
    }

    getAssetsLiabilities(_applicationId: number): Observable<any> {
        return this.http.get<any>(environment.depositApiUrl + `/financialcondition/${_applicationId}`);
    }

    addAddress(_applicantId: number, data): Observable<any> {
        return this.http.post<any>(API_APPLICANT_URL + `/address/${_applicantId}`, data);
    }

    updateCoApplicant(_appId: number, body: any): Observable<any> {
        return this.http.put<any>(environment.apiUrl + `/coapplicant/${_appId}`, body);
    }

    /*=============== Prove integration ==============*/
    memberAuth(data: any) {
        return this.http.post<any>(environment.apiUrl + `/member/authurl`, data,
            {headers: {'X-Skip-Interceptor': environment.auth.interceptorSkipHeader}});
    }

    searchNaicsCompanyByName(s: string): Observable<any> {
        const url = 'https://7MV7A9XWSE.algolia.net/1/indexes/NAICS/query';
        const body = {
            query: `${s}`,
            filters: 'institutionlookup:NAICS_Lookup',
            attributesToRetrieve: [
                'code',
                'value'
            ]
        };

        return this.http.post(url, body, {
            headers: {
                'X-Skip-Headers': environment.auth.interceptorSkipHeader,
                'X-Algolia-Api-Key': environment.ALGOLIA_API_KEY,
                'X-Algolia-Application-Id': environment.ALGOLIA_APPLICATION_ID
            }
        });
    }

    addExtendedAttributesForApplicant(applicantId: number, extendedAttributes: ExtendedAttributesModel[]): Observable<CSPostResponseModel> {
        return this.http.post<CSPostResponseModel>(environment.apiUrl + `/applicant/extendedattributes/${applicantId}`, extendedAttributes);
    }
}
